import { Button } from '#/components/Button/Button';
import { Icon } from '#/components/Icon/Icon';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { H6 } from '#/components/Text/H6';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { ListingSearchType } from '#/modules/cms/api/types/CmsApiDocument';
import { searchGetJobsPath } from '#/modules/search/jobs/routing/searchGetJobsPath';
import { searchGetVolopsPath } from '#/modules/search/volops/routing/searchGetVolopsPath';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { showModal } from '#/zustand-stores/modalStore';

import {
  CmsSocialSidebarCommentsButtonContainer,
  CmsSocialSidebarContainer,
  CmsSocialSidebarFAQLinkContainerBase,
  CmsSocialSidebarNewsletterButtonContainer,
  CmsSocialSidebarRecentJobsContainer,
  CmsSocialSidebarShareButtonContainer,
  CmsSocialSidebarTooltipContainer,
} from './CmsSocialSidebar.styles';

type Props = {
  listingSearchType: ListingSearchType;
  hasSocialShare: boolean;
  hasComments: boolean;
  subscribe: () => void;
  canSubscribe: boolean;
  isSubscribed: boolean;
  shareUrlByLocale: Record<I18nLocale, string>;
  subsiteSubscribeText: string;
};

export function CmsSocialSidebar({
  canSubscribe,
  isSubscribed,
  subscribe,
  listingSearchType,
  hasSocialShare,
  hasComments,
  shareUrlByLocale,
  subsiteSubscribeText,
}: Props) {
  const { i18n, locale } = useI18n();

  const handleShare = () => {
    showModal('SHARE_LISTING', {
      listingType: 'URL',
      messageOverride: i18n('Check out this article'),
      shareUrl: shareUrlByLocale,
    });
  };

  if (!canSubscribe && !hasSocialShare) {
    return null;
  }

  const goToComments = () => {
    const element = document.getElementById('openweb-container');
    element?.scrollIntoView();
  };

  let recipesForActionUrl = '/en/recipes-for-action/submission-form';
  if (locale === I18nLocale.ES) {
    recipesForActionUrl = '/es/recetas-para-accion/formulario';
  }

  const searchListingMap: Record<
    ListingSearchType,
    { headline: string; description: string; buttonText: string; url: string }
  > = {
    JOB: {
      headline: i18n('Land Your Dream Job'),
      description: i18n('Careers that help you move from intention to action'),
      buttonText: i18n('Search Jobs'),
      url: searchGetJobsPath({
        locale,
        filtersByName: { locale },
        embedSearchWidgetId: null,
      }),
    },
    VOLOP: {
      headline: i18n('Make a Difference'),
      description: i18n('Search for volunteer opportunities in your community'),
      buttonText: i18n('Search'),
      url: searchGetVolopsPath({
        locale,
        filtersByName: { locale },
        embedSearchWidgetId: null,
      }),
    },
    RECIPES_FOR_ACTION: {
      headline: i18n('Submit a Recipe'),
      description: i18n(
        'Share how you’ve made a difference in your community to inspire others.',
      ),
      buttonText: i18n('Submit a Recipe'),
      url: recipesForActionUrl,
    },
  };

  return (
    <CmsSocialSidebarContainer>
      {listingSearchType in searchListingMap ? (
        <CmsSocialSidebarRecentJobsContainer>
          <H6 lineHeight={1} mb="16px">
            {searchListingMap[listingSearchType].headline}
          </H6>
          <BodyMicro lineHeight={1.2} mb="16px">
            {searchListingMap[listingSearchType].description}
          </BodyMicro>
          <Button
            type="link-with-state"
            qaId="sidebar-recent-listings-button"
            onClick={() =>
              trackClicked(searchListingMap[listingSearchType].buttonText, {
                context: 'CMS Page',
              })
            }
            to={searchListingMap[listingSearchType].url}
            state={{ filterType: listingSearchType }}
            variant="primary"
            size="small"
          >
            {searchListingMap[listingSearchType].buttonText}
          </Button>
        </CmsSocialSidebarRecentJobsContainer>
      ) : null}
      <CmsSocialSidebarTooltipContainer>
        {canSubscribe ? (
          <Tooltip
            variant="filled"
            content={
              isSubscribed
                ? i18n('You are now subscribed!')
                : subsiteSubscribeText
            }
          >
            <CmsSocialSidebarNewsletterButtonContainer
              type="button"
              onClick={subscribe}
              data-qa-id="sidebar-subscribe-to-subsite"
              disabled={isSubscribed}
              aria-label={i18n('Subscribe')}
            >
              <Icon name="newsletter" size={30} />
            </CmsSocialSidebarNewsletterButtonContainer>
          </Tooltip>
        ) : null}
        {hasSocialShare ? (
          <Tooltip variant="filled" content={i18n('Share')}>
            <CmsSocialSidebarShareButtonContainer
              type="button"
              onClick={handleShare}
              data-qa-id="sidebar-social-share-button"
              aria-label={i18n('Share')}
            >
              <Icon name="share" size={24} />
            </CmsSocialSidebarShareButtonContainer>
          </Tooltip>
        ) : null}
        {hasComments ? (
          <Tooltip variant="filled" content={i18n('Comments')}>
            <CmsSocialSidebarCommentsButtonContainer
              type="button"
              onClick={() => {
                trackClicked('Comments CTA', { context: 'floating_cta' });
                goToComments();
              }}
              data-qa-id="sidebar-comments-button"
              aria-label={i18n('Comments')}
            >
              <Icon name="speech-bubble" size={24} />
            </CmsSocialSidebarCommentsButtonContainer>
          </Tooltip>
        ) : null}
        {listingSearchType === 'RECIPES_FOR_ACTION' ? (
          <Tooltip
            variant="filled"
            content={i18n('Frequently Asked Questions (FAQ)')}
          >
            {/* !!!: wrapped with div as Tooltip doesn't seem to detect
                     the Link component as a valid child
             */}
            <div>
              <CmsSocialSidebarFAQLinkContainerBase
                to="en/recipes-for-action/faq"
                qaId="sidebar-faq-button"
                aria-label={i18n('Frequently Asked Questions (FAQ)')}
              >
                <Icon name="question-circle" size={24} />
              </CmsSocialSidebarFAQLinkContainerBase>
            </div>
          </Tooltip>
        ) : null}
      </CmsSocialSidebarTooltipContainer>
    </CmsSocialSidebarContainer>
  );
}
